import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import Backend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { applyD3TimeLocale } from '../common/utils/d3Locale';
import { applyMomentLocale } from './momentLocale';
import { createGatedLogger } from './gatedLogger';

const LANGUAGE_STORAGE_KEY = 'petasenseLng';

// Translations load asynchronously via http-backend, so the t() calls that `*.constants.js`
// modules make at import time run before the en bundle has landed. See gatedLogger.js for why
// that produces a flood of false-positive `missingKey undefined ...` lines and which channels
// they arrive on; the gate re-opens as soon as the bundle is loaded, so genuinely missing keys
// discovered during normal use are still reported.
let translationsReady = false;
const gatedLogger = createGatedLogger(() => translationsReady);

// Active language follows user `locale` from the API; I18nLanguageSync calls changeLanguage(lng).
export const i18nInitPromise = i18n
  .use(gatedLogger)
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Default language and fallback
    fallbackLng: 'en',
    // IMPORTANT: don't set `lng` here; let LanguageDetector read from localStorage.

    // Debug mode: enable in development to catch missing keys
    debug: process.env.NODE_ENV === 'development',

    // Interpolation configuration (equivalent to Flask-Babel variable substitution)
    interpolation: {
      escapeValue: false, // React already escapes by default
      formatSeparator: ',',
      format(value, format) {
        if (format === 'uppercase') return value.toUpperCase();
        if (format === 'lowercase') return value.toLowerCase();
        return value;
      }
    },

    // Key separator for nested keys (e.g., 'login.email')
    keySeparator: '.',

    // Namespace separator
    nsSeparator: ':',

    // Default namespace
    defaultNS: 'translation',

    // Namespaces to load
    ns: ['translation'],

    // React-specific configuration
    react: {
      useSuspense: false, // Set to false for older React versions
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed',
      transEmptyNodeValue: '',
      transSupportBasicHtmlNodes: true,
      transWrapTextNodes: '',
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p', 'b', 'em'],
    },

    // Backend configuration (where translation files are loaded from)
    // PUBLIC_URL is used when app is deployed with a base path (e.g. /app/)
    // `v` cache-busts the CDN/browser cache on every deploy: translation.json is a plain
    // public/ file with no webpack content-hash, so without a version param, browsers/CDNs
    // that already cached an older copy never pick up newly-added keys (they silently fall
    // back to defaultValue/English) until that cache naturally expires.
    // REACT_APP_BUILD_ID changes on every build: the Jenkinsfile injects env.BUILD_NUMBER for
    // CI/deploys, and the `build-js` npm script defaults it to a unix timestamp ($(date +%s))
    // for local/manual `npm run build:*` — so both deployed and local artifacts cache-bust.
    // The `|| NODE_ENV` tail is only a last-resort default (e.g. the dev server, where no build
    // step runs); it's a constant and does not cache-bust, which is fine for local dev.
    backend: {
      loadPath: `${process.env.PUBLIC_URL || ''}/locales/{{lng}}/{{ns}}.json?v=${process.env.REACT_APP_BUILD_ID || process.env.NODE_ENV}`,
      crossDomain: false,
    },

    // Keep untranslated pages in the last confirmed app language.
    // First-time visitors have no stored value, so fallbackLng keeps them on English.
    detection: {
      order: ['localStorage'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },

    // Pluralization configuration (equivalent to Flask-Babel ngettext)
    // i18next automatically handles pluralization based on language rules
    // Usage: t('key', { count: 1 }) for singular, t('key', { count: 2 }) for plural
    // In translation files: "key": "item" and "key_plural": "items"

    // Match API `locale` and folders under public/locales/ (Japanese: `ja`).
    supportedLngs: ['en', 'es', 'fr', 'de', 'ja'],

    // Clean code (use language codes like 'en' instead of 'en-US')
    cleanCode: true,

    // Non-explicit supported languages will fallback to this
    nonExplicitSupportedLngs: true,

    // Allow t() to return objects/arrays (needed by TermsPage for SaaS agreement content)
    // returnObjects: true,
  });

// Once the initial translation bundle has loaded, allow `missingKey` warnings through again so
// genuinely missing keys discovered during normal use are still surfaced in development.
i18nInitPromise.then(() => { translationsReady = true; });
i18n.on('loaded', () => { translationsReady = true; });

// Initialize D3 time locale and moment for the default language.
const initialLng = i18n.language || 'en';
applyD3TimeLocale(initialLng);
applyMomentLocale(initialLng);

// Keep D3 time locale and moment in sync with i18n (I18nLanguageSync → changeLanguage).
i18n.on('languageChanged', (lng) => {
  applyD3TimeLocale(lng);
  applyMomentLocale(lng);
  try {
    if (lng) localStorage.setItem(LANGUAGE_STORAGE_KEY, String(lng));
  } catch (_e) {
    // ignore storage failures (private mode / denied)
  }
});

export default i18n;
